import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";

export interface Retailer {
  id: string;
  org_id: string;
  name: string;
  contact_email: string | null;
  logo: string | null;
  active: boolean;
  email_domains?: string[];
  contact_emails?: string[];
  sync_enabled?: boolean;
  extn: Record<string, any>;
  created_at: string;
}

export interface RetailerFormData {
  name: string;
  contact_email?: string;
  logo?: string;
  active?: boolean;
  email_domains?: string[];
  contact_emails?: string[];
  sync_enabled?: boolean;
}

/**
 * Upload retailer logo to Supabase storage
 */
export const uploadRetailerLogo = async (
  file: File
): Promise<{ success: boolean; url?: string; error?: string }> => {
  try {
    const orgId = await getUserOrganizationId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    // Generate unique filename
    const fileExt = file.name.split(".").pop();
    const fileName = `${orgId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

    // Upload to Supabase storage
    const { data, error } = await supabase.storage
      .from("retailersLogo")
      .upload(fileName, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (error) throw error;

    // Get public URL
    const { data: urlData } = supabase.storage
      .from("retailersLogo")
      .getPublicUrl(fileName);

    return { success: true, url: urlData.publicUrl };
  } catch (error: any) {
    console.error("Error uploading logo:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete retailer logo from Supabase storage
 */
export const deleteRetailerLogo = async (
  logoUrl: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Extract file path from URL
    const urlParts = logoUrl.split("/retailersLogo/");
    if (urlParts.length < 2) {
      return { success: false, error: "Invalid logo URL" };
    }
    const filePath = urlParts[1];

    // Delete from storage
    const { error } = await supabase.storage
      .from("retailersLogo")
      .remove([filePath]);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error("Error deleting logo:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user's organization ID
 */
const getUserOrganizationId = async (): Promise<string | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: orgMember, error } = await supabase
      .from("base_organisation_members")
      .select("organisation_id")
      .eq("user_id", user.id)
      .limit(1)
      .single();

    if (error) {
      console.error("Error getting org ID:", error);
      return null;
    }

    return orgMember?.organisation_id || null;
  } catch (error) {
    console.error("Error getting organization ID:", error);
    return null;
  }
};

/**
 * Get all retailers for the user's organization
 */
export const getRetailers = async (): Promise<{
  success: boolean;
  data?: Retailer[];
  error?: string;
}> => {
  try {
    const orgId = await getUserOrganizationId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    const { data, error } = await supabase
      .from("retailer")
      .select("*")
      .eq("org_id", orgId)
      .order("name", { ascending: true });

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error: any) {
    console.error("Error fetching retailers:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get active retailers only
 */
export const getActiveRetailers = async (): Promise<{
  success: boolean;
  data?: Retailer[];
  error?: string;
}> => {
  try {
    const orgId = await getUserOrganizationId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    const { data, error } = await supabase
      .from("retailer")
      .select("*")
      .eq("org_id", orgId)
      .eq("active", true)
      .order("name", { ascending: true });

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error: any) {
    console.error("Error fetching active retailers:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a new retailer
 */
export const createRetailer = async (
  formData: RetailerFormData
): Promise<{
  success: boolean;
  data?: Retailer;
  error?: string;
}> => {
  try {
    const orgId = await getUserOrganizationId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    const { data, error } = await supabase
      .from("retailer")
      .insert({
        org_id: orgId,
        name: formData.name,
        contact_email: formData.contact_email || null,
        logo: formData.logo || null,
        active: formData.active !== undefined ? formData.active : true,
        email_domains: formData.email_domains || [],
        contact_emails: formData.contact_emails || [],
        sync_enabled: formData.sync_enabled !== undefined ? formData.sync_enabled : true,
        extn: {},
        created_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error("Error creating retailer:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Update an existing retailer
 */
export const updateRetailer = async (
  id: string,
  formData: Partial<RetailerFormData>
): Promise<{
  success: boolean;
  data?: Retailer;
  error?: string;
}> => {
  try {
    const updateData: any = {};

    if (formData.name !== undefined) updateData.name = formData.name;
    if (formData.contact_email !== undefined) updateData.contact_email = formData.contact_email || null;
    if (formData.logo !== undefined) updateData.logo = formData.logo || null;
    if (formData.active !== undefined) updateData.active = formData.active;
    if (formData.email_domains !== undefined) updateData.email_domains = formData.email_domains;
    if (formData.contact_emails !== undefined) updateData.contact_emails = formData.contact_emails;
    if (formData.sync_enabled !== undefined) updateData.sync_enabled = formData.sync_enabled;

    const { data, error } = await supabase
      .from("retailer")
      .update(updateData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error("Error updating retailer:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a retailer
 */
export const deleteRetailer = async (
  id: string
): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const { error } = await supabase.from("retailer").delete().eq("id", id);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error("Error deleting retailer:", error);
    return { success: false, error: error.message };
  }
};

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const API_KEY = import.meta.env.VITE_API_KEY || 'scopingai';

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { user } } = await supabase.auth.getUser();
  return {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    ...(user?.id ? { 'x-user-id': user.id } : {}),
  };
}

export type SectionKey = 'energy_rates' | 'contract_terms' | 'environmental_charges' | 'non_energy_charges';

export type RetailerParsingPrompts = {
  offer_parsing_prompt: string | null;
  section_parsing_prompts: Partial<Record<SectionKey, string>> | null;
  flag_active: boolean;
};

export const getRetailerParsingPrompts = async (
  retailerId: string
): Promise<{ success: boolean; data?: RetailerParsingPrompts; error?: string }> => {
  try {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/scopingai/retailers/${retailerId}/parsing-prompts`, { headers });
    if (!res.ok) throw new Error(await res.text());
    return { success: true, data: await res.json() };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const saveRetailerParsingPrompts = async (
  retailerId: string,
  data: { offer_parsing_prompt?: string | null; section_parsing_prompts?: Partial<Record<SectionKey, string>> | null; flag_active?: boolean }
): Promise<{ success: boolean; error?: string }> => {
  try {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/scopingai/retailers/${retailerId}/parsing-prompts`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await res.text());
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Get retailer by ID
 */
export const getRetailerById = async (
  id: string
): Promise<{
  success: boolean;
  data?: Retailer;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from("retailer")
      .select("*")
      .eq("id", id)
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error("Error fetching retailer:", error);
    return { success: false, error: error.message };
  }
};


// ─── Bill validation parsing prompts ─────────────────────────────────────────
// Separate from the offer prompts above on purpose. Different product, different
// section vocabulary, different on/off flag — and separate endpoints so one save
// can never clear the other feature's prompts.

/**
 * Bill invoice sections. NOT `SectionKey` — wiring the bill editor to the tender
 * vocabulary is the most likely bug in this feature and it looks correct in
 * review, so the two lists never share a type.
 */
export type BillSectionKey =
  | 'retail' | 'distribution' | 'metering' | 'environmental' | 'other' | 'gst';

export const BILL_SECTION_KEYS: BillSectionKey[] =
  ['retail', 'distribution', 'metering', 'environmental', 'other', 'gst'];

export type RetailerBillParsingPrompts = {
  bill_parsing_prompt: string | null;
  bill_section_parsing_prompts: Partial<Record<BillSectionKey, string>> | null;
  bill_prompt_active: boolean;
};

export const getRetailerBillParsingPrompts = async (
  retailerId: string
): Promise<{ success: boolean; data?: RetailerBillParsingPrompts; error?: string }> => {
  try {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/scopingai/retailers/${retailerId}/bill-parsing-prompts`, { headers });
    if (!res.ok) throw new Error(await res.text());
    return { success: true, data: await res.json() };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const saveRetailerBillParsingPrompts = async (
  retailerId: string,
  data: {
    bill_parsing_prompt?: string | null;
    bill_section_parsing_prompts?: Partial<Record<BillSectionKey, string>> | null;
    bill_prompt_active?: boolean;
  }
): Promise<{ success: boolean; error?: string }> => {
  try {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/scopingai/retailers/${retailerId}/bill-parsing-prompts`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await res.text());
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};
